"use client";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  AcademicCapIcon,
  CheckCircleIcon,
  HandRaisedIcon,
  RocketLaunchIcon,
  SparklesIcon,
  SunIcon,
  UserGroupIcon,
  HeartIcon,
  TrophyIcon
} from "@heroicons/react/20/solid";

import { useSEO } from "../../hooks/useSEO";
import axiosInstance from "../../api/axiosInstance";
import { useAppSelector } from "../../store/hooks";

const values = [
  {
    name: "World-Class Care.",
    description:
      "We provide elite-level medical care and rehabilitation technology used by professional athletes.",
    icon: TrophyIcon,
  },
  {
    name: "Commitment to Recovery.",
    description:
      "Your health is our priority. We are dedicated to seeing you return to your peak performance safely.",
    icon: HandRaisedIcon,
  },
  {
    name: "Patient-Centered Support.",
    description:
      "We support you through every step of your healing process with empathy and clear communication.",
    icon: UserGroupIcon,
  },
  {
    name: "Evidence-Based Medicine.",
    description:
      "Our treatments are grounded in the latest sports science research and clinical studies.",
    icon: AcademicCapIcon,
  },
  {
    name: "Holistic Wellbeing.",
    description:
      "We treat the whole athlete, addressing physical optimization, nutrition, and mental resilience.",
    icon: HeartIcon,
  },
  {
    name: "Prevention Focus.",
    description:
      "We don't just treat injuries; we help you build the strength and habits to prevent them.",
    icon: SunIcon,
  },
];

const features = [
  "Expert Diagnostics",
  "Personalized Rehab Plans",
  "Advanced Physiotherapy",
  "Nutritional Guidance",
  "Performance Analysis",
  "Minimally Invasive Treatments",
];

export default function AboutUs(): JSX.Element {
  const [doctors, setDoctors] = useState([]);
  const user = useAppSelector((state) => state.auth.user);
  const destination = user ? "/dashboard" : "/appointment";

  // useEffect(() => {
  //   axiosInstance.get("/appointment/doctors-list").then((res) => {
  //     setDoctors(res.data);
  //   });
  // }, []);

  useSEO({
    title: "About Us | Sports Medicine Specialists in Anna Nagar",
    description: "Meet our top-rated team of doctors, therapists, and sports medicine specialists in Chennai. We blend advanced medical techniques with personalized care.",
    keywords: "sports medicine specialists chennai, orthopedic doctors anna nagar, sports physiotherapy near me"
  });

  return (
    <div className="bg-gray-50 text-gray-800">
      <section className="relative bg-indigo-600 text-white py-24 overflow-hidden min-h-[600px] flex items-center justify-center">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/sports-psychology.png"
            alt="About our clinic"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-indigo-900/60 mix-blend-multiply" />
        </div>
        <motion.div
          className="relative z-10 max-w-6xl mx-auto px-6 text-center"
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
            Elevating Sports Medicine
          </h1>
          <p className="text-lg md:text-xl max-w-3xl mx-auto text-indigo-50 mb-10 font-light leading-relaxed">
            Dedicated to excellence in therapy, rehabilitation, and performance care.
            Our multidisciplinary team combines extremely advanced diagnostics with
            comprehensive treatment plans to help you recover faster and perform better.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href={destination}
              target={destination === '/appointment' ? '_blank' : undefined}
              rel={destination === '/appointment' ? 'noopener noreferrer' : undefined}
              className="rounded-full bg-white px-8 py-3.5 text-sm font-semibold text-indigo-600 shadow-sm hover:bg-indigo-50 transition-colors duration-200"
            >
              Book Appointment
            </a>
            <a
              href="#team"
              className="rounded-full bg-indigo-500/20 backdrop-blur-sm border border-indigo-200/30 px-8 py-3.5 text-sm font-semibold text-white hover:bg-indigo-500/30 transition-colors duration-200"
            >
              Meet Our Specialists
            </a>
          </div>
        </motion.div>
      </section>
      {/* Feature section */}
      <div className="mx-auto mt-32 max-w-7xl px-6 sm:mt-40 lg:px-8">
        <div className="mx-auto max-w-2xl lg:mx-0">
          <h2 className="text-4xl font-semibold tracking-tight text-pretty text-gray-900 sm:text-5xl">
            Our Philosophy
          </h2>
          <p className="mt-6 text-lg/8 text-gray-700">
            We are guided by principles that prioritize patient health, innovation
            in therapy, and holistic well-being.
          </p>
        </div>
        <dl className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-8 text-base/7 text-gray-600 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3 lg:gap-x-16">
          {values.map((value) => (
            <div key={value.name} className="relative pl-9">
              <dt className="inline font-semibold text-gray-900">
                <value.icon
                  aria-hidden="true"
                  className="absolute top-1 left-1 size-5 text-indigo-600"
                />
                {value.name}
              </dt>{" "}
              <dd className="inline">{value.description}</dd>
            </div>
          ))}
        </dl>
      </div>

      {/* Team section */}
      {/* <div id="team" className="mx-auto mt-32 max-w-7xl px-6 sm:mt-40 lg:px-8">
        <div className="mx-auto max-w-2xl lg:mx-0">
          <h2 className="text-4xl font-semibold tracking-tight text-pretty text-gray-900 sm:text-5xl">
            Our Team
          </h2>
          <p className="mt-6 text-lg/8 text-gray-600">
            Meet the specialists dedicated to your recovery and performance.
          </p>
        </div>
        <ul
          role="list"
          className="mx-auto mt-20 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3 xl:grid-cols-4"
        >
          {doctors.map((person: any) => (
            <li key={person.name}>
              <img
                alt=""
                src={person.profile ? person.profile : "/images/doctor-default.jpeg"}
                className="h-72 aspect-14/13 w-full rounded-2xl object-cover outline-1 -outline-offset-1 outline-black/5"
              />
              <h3 className="mt-6 text-lg/8 font-semibold tracking-tight text-gray-900">
                {person.name}
              </h3>
              <p className="text-base/7 text-gray-600">Specialisation: {person.specialisation}</p>
              <p className="text-sm/6 text-gray-500">Treatments: {person?.treatments?.length ? person.treatments?.map((t: any) => t.name).join(", ") : "--"}</p>
            </li>
          ))}
        </ul>
      </div> */}

      {/* CTA section */}
      <div className="relative isolate mt-32 sm:mt-40"> {/* Removed -z-10 which was hiding content */}
        <div className="mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="mx-auto flex max-w-2xl flex-col gap-16 bg-white/75 px-6 py-16 shadow-lg ring-1 ring-gray-900/5 sm:rounded-3xl sm:p-8 lg:mx-0 lg:max-w-none lg:flex-row lg:items-center lg:py-20 xl:gap-x-20 xl:px-20">
            <img
              alt="Physiotherapy session"
              src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80"
              className="h-96 w-full flex-none rounded-2xl object-cover lg:aspect-square lg:h-auto lg:max-w-sm"
            />
            <div className="w-full flex-auto">
              <h2 className="text-4xl font-semibold tracking-tight text-pretty text-gray-950 sm:text-5xl">
                Ready to recover?
              </h2>
              <p className="mt-6 text-lg/8 text-pretty text-gray-600">
                Don't let pain hold you back. Schedule a consultation with our experts today and start your journey back to peak performance.
              </p>
              <ul
                role="list"
                className="mt-10 grid grid-cols-1 gap-x-8 gap-y-3 text-base/7 text-gray-950 sm:grid-cols-2"
              >
                {features.map((feature) => (
                  <li key={feature} className="flex gap-x-3">
                    <CheckCircleIcon
                      aria-hidden="true"
                      className="h-7 w-5 flex-none text-indigo-500"
                    />
                    {feature}
                  </li>
                ))}
              </ul>
              <div className="mt-10 flex gap-x-6">
                <a
                  href={destination}
                  target={destination === '/appointment' ? '_blank' : undefined}
                  rel={destination === '/appointment' ? 'noopener noreferrer' : undefined}
                  className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                >
                  Book Appointment
                </a>
                {/* <a
                  href="#team"
                  className="text-sm/6 font-semibold text-indigo-600 hover:text-indigo-900 flex items-center"
                >
                  View Doctors <span aria-hidden="true" className="ml-1">&rarr;</span>
                </a> */}
              </div>
            </div>
          </div>
        </div>
        <div
          aria-hidden="true"
          className="absolute inset-x-0 -top-16 -z-10 flex transform-gpu justify-center overflow-hidden blur-3xl"
        >
          <div
            style={{
              clipPath:
                "polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)",
            }}
            className="aspect-1318/752 w-329.5 flex-none bg-linear-to-r from-[#9fd6fc] to-[#8680fd] opacity-50"
          />
        </div>
      </div>
    </div>
  );
}
